import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import {
  useGetWebhookEndpointsQuery,
  useCreateWebhookEndpointMutation,
  useUpdateWebhookEndpointMutation,
  useDeleteWebhookEndpointMutation,
  useTestWebhookEndpointMutation,
  useToggleWebhookEndpointStatusMutation,
} from "../store/api";
import {
  WebhookEndpoint,
  WebhookEndpointFilters,
  CreateWebhookEndpointRequest,
  UpdateWebhookEndpointRequest,
} from "../types/webhook";
import WebhookEndpointsTable from "../components/webhooks/WebhookEndpointsTable";
import WebhookEndpointModal from "../components/webhooks/WebhookEndpointModal";
import WebhookEndpointFiltersComponent from "../components/webhooks/WebhookEndpointFilters";
import WebhookEndpointLogsModal from "../components/webhooks/WebhookEndpointLogsModal";
import { useNotification } from "../hooks/useNotification";

const WebhookEndpointsPage: React.FC = () => {
  const { t } = useTranslation("webhooks");
  const { showNotification } = useNotification();
  const [searchParams] = useSearchParams();

  const [filters, setFilters] = useState<WebhookEndpointFilters>({
    sort_by: "created_at",
    sort_order: "desc",
    per_page: 15,
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEndpoint, setEditingEndpoint] =
    useState<WebhookEndpoint | null>(null);
  const [isLogsModalOpen, setIsLogsModalOpen] = useState(false);
  const [selectedEndpointForLogs, setSelectedEndpointForLogs] = useState<{
    id: number;
    name: string;
  } | null>(null);

  // Читаем webhook_id из URL параметров при загрузке страницы
  useEffect(() => {
    const webhookId = searchParams.get("webhook_id");
    if (webhookId) {
      setFilters((prev) => ({
        ...prev,
        webhook_id: parseInt(webhookId),
      }));
    }
  }, [searchParams]);

  // API queries and mutations
  const {
    data: endpointsData,
    isLoading,
    refetch,
  } = useGetWebhookEndpointsQuery(filters);
  const [createEndpoint, { isLoading: isCreating }] =
    useCreateWebhookEndpointMutation();
  const [updateEndpoint, { isLoading: isUpdating }] =
    useUpdateWebhookEndpointMutation();
  const [deleteEndpoint] = useDeleteWebhookEndpointMutation();
  const [testEndpoint] = useTestWebhookEndpointMutation();
  const [toggleStatus] = useToggleWebhookEndpointStatusMutation();

  const handleCreateEndpoint = async (data: CreateWebhookEndpointRequest) => {
    try {
      await createEndpoint(data).unwrap();
      showNotification(t("endpoints.messages.created"), "success");
      setIsModalOpen(false);
      refetch();
    } catch (error: any) {
      showNotification(
        error.data?.message || t("endpoints.messages.create_error"),
        "error"
      );
    }
  };

  const handleUpdateEndpoint = async (data: UpdateWebhookEndpointRequest) => {
    if (!editingEndpoint) return;

    try {
      await updateEndpoint({ id: editingEndpoint.id, data }).unwrap();
      showNotification(t("endpoints.messages.updated"), "success");
      setIsModalOpen(false);
      setEditingEndpoint(null);
      refetch();
    } catch (error: any) {
      showNotification(
        error.data?.message || t("endpoints.messages.update_error"),
        "error"
      );
    }
  };

  const handleDeleteEndpoint = async (id: number) => {
    if (!window.confirm(t("endpoints.messages.confirm_delete"))) return;

    try {
      await deleteEndpoint(id).unwrap();
      showNotification(t("endpoints.messages.deleted"), "success");
      refetch();
    } catch (error: any) {
      showNotification(
        error.data?.message || t("endpoints.messages.delete_error"),
        "error"
      );
    }
  };

  const handleTestEndpoint = async (id: number) => {
    try {
      await testEndpoint(id).unwrap();
      showNotification(t("endpoints.messages.test_success"), "success");
    } catch (error: any) {
      showNotification(
        error.data?.message || t("endpoints.messages.test_error"),
        "error"
      );
    }
  };

  const handleToggleStatus = async (id: number) => {
    try {
      await toggleStatus(id).unwrap();
      showNotification(t("endpoints.messages.status_toggled"), "success");
      refetch();
    } catch (error: any) {
      showNotification(
        error.data?.message || t("endpoints.messages.toggle_error"),
        "error"
      );
    }
  };

  const handleViewLogs = (id: number, name: string) => {
    setSelectedEndpointForLogs({ id, name });
    setIsLogsModalOpen(true);
  };

  const handleEditEndpoint = (endpoint: WebhookEndpoint) => {
    setEditingEndpoint(endpoint);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingEndpoint(null);
  };

  const handleCloseLogsModal = () => {
    setIsLogsModalOpen(false);
    setSelectedEndpointForLogs(null);
  };

  const handleSubmit = (
    data: CreateWebhookEndpointRequest | UpdateWebhookEndpointRequest
  ) => {
    if (editingEndpoint) {
      handleUpdateEndpoint(data as UpdateWebhookEndpointRequest);
    } else {
      handleCreateEndpoint(data as CreateWebhookEndpointRequest);
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t("endpoints.title")}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">{t("endpoints.description")}</p>
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          {t("endpoints.create")}
        </button>
      </div>

      <WebhookEndpointFiltersComponent
        filters={filters}
        onFiltersChange={setFilters}
      />

      <WebhookEndpointsTable
        endpoints={endpointsData?.data || []}
        onEdit={handleEditEndpoint}
        onDelete={handleDeleteEndpoint}
        onTest={handleTestEndpoint}
        onToggleStatus={handleToggleStatus}
        onViewLogs={handleViewLogs}
        isLoading={isLoading}
      />

      <WebhookEndpointModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        endpoint={editingEndpoint}
        webhookId={filters.webhook_id}
        onSubmit={handleSubmit}
        isLoading={isCreating || isUpdating}
      />

      {/* Logs Modal */}
      {isLogsModalOpen && selectedEndpointForLogs && (
        <WebhookEndpointLogsModal
          isOpen={isLogsModalOpen}
          onClose={handleCloseLogsModal}
          endpointId={selectedEndpointForLogs.id}
          endpointName={selectedEndpointForLogs.name}
        />
      )}
    </div>
  );
};

export default WebhookEndpointsPage;
